// @ts-check
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { whatsappApi } from "@/api/endpoints/whatsapp.js";
import { toast } from "@/shared/ui/toaster.jsx";

const chaves = {
  config: ["whatsapp", "config"],
  conversas: ["whatsapp", "conversations"],
  logs: (filtros) => ["whatsapp", "logs", filtros ?? {}],
};

export function useWhatsappConfig() {
  return useQuery({
    queryKey: chaves.config,
    queryFn: async () => (await whatsappApi.getConfig()).config,
  });
}

/** Conversas vindas do número oficial (Meta), agrupadas por contato. */
export function useMetaConversations() {
  return useQuery({
    queryKey: chaves.conversas,
    queryFn: async () => (await whatsappApi.conversations()).conversations,
    // A Meta entrega por webhook; recarregamos periodicamente para a inbox
    // não ficar parada enquanto a aba está aberta.
    refetchInterval: 30_000,
  });
}

/** @param {{ eventType?: string, limit?: number }} [filtros] */
export function useWhatsappLogs(filtros) {
  return useQuery({
    queryKey: chaves.logs(filtros),
    queryFn: async () => (await whatsappApi.logs(filtros)).logs,
  });
}

export function useSaveWhatsappConfig() {
  const qc = useQueryClient();
  return useMutation({
    /** @param {Record<string, string>} dados */
    mutationFn: async (dados) => (await whatsappApi.saveConfig(dados)).config,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["whatsapp"] });
      toast.success("Configuração salva. Teste a conexão para ativar.");
    },
    onError: (erro) => toast.error(erro.message),
  });
}

export function useTestWhatsappConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => whatsappApi.testConnection(),
    onSuccess: (resultado) => {
      qc.invalidateQueries({ queryKey: ["whatsapp"] });
      if (resultado.success) toast.success("Conexão com a Meta validada.");
      else toast.error("A Meta recusou as credenciais. Veja os detalhes abaixo.");
    },
    onError: (erro) => toast.error(erro.message),
  });
}

export function useSendMetaMessage() {
  const qc = useQueryClient();
  return useMutation({
    /** @param {{ to: string, message: string, leadId?: string|null }} dados */
    mutationFn: (dados) => whatsappApi.sendMessage(dados),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chaves.conversas });
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (erro) => toast.error(erro.message),
  });
}
